import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import type { SvgIconComponent } from '@mui/icons-material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Oth360Banner } from 'src/components/oth/Oth360Banner';

import { developmentPlanApi } from 'src/api/developmentPlan';
import type { HandlingsCoachV2Section } from 'src/contracts/oth';
import useAppSelector from 'src/hooks/Redux/useAppSelector';
import { selectCurrentUserId } from 'src/redux/slices/loggedUserSlice';
import styles from './DevelopmentPlan.module.scss';
import {
  AI_COACH_BACKGROUND_WATCH_MS,
  AI_COACH_RETRYING_USER_MESSAGE,
  AI_COACH_STILL_RUNNING_USER_MESSAGE,
  analyzeHandlingsCoachBox,
  getAiCoachUserErrorMessage,
  isAnalyzeButtonBusy,
  pickCachedSection,
  waitForCachedAiSection,
  type AnalyzeBoxJobUiStatus,
} from './analyzeHandlingsCoachBox';
import { AddAllStepsDialog } from './calendar/AddAllStepsDialog';
import { AddStepToCalendar } from './calendar/AddStepToCalendar';
import { addDays } from './calendar/dates';
import {
  collectCalendarSteps,
  developmentPlanUrl,
  type CalendarStep,
} from './calendar/steps';
import { StepNavigation } from './PlanStepNavigation';
import { downloadAnalysisPdf } from './utils/downloadAnalysisPdf';
import { downloadDevelopmentPlanPdf } from './utils/downloadDevelopmentPlanPdf';
const buildBoxId = (topId: TopId, subId: SubId) => `${topId}:${subId}` as const;

const analysisBoxDomId = (boxId: string) =>
  `devplan-analysis-${boxId.split(':').join('--')}`;

type TopId =
  | 'ambisjonsprosess'
  | 'lagorganisering'
  | 'prestasjonskultur'
  | 'ledelsePersonlig';

type MidId = 'now' | 'desired' | 'actions';

type TabId = MidId | 'summary';

type SubId =
  | 'visjon'
  | 'misjon'
  | 'delmaal'
  | 'hundreAnsvar'
  | 'samspillsoner'
  | 'verdier'
  | 'feedback'
  | 'utfordringer'
  | 'coaching';

type TopDef = {
  id: TopId;
  title: string;
  subs: Array<{ id: SubId; title: string }>;
};

const midDefs: Array<{ id: MidId; title: string }> = [
  { id: 'now', title: 'Nå-tilstand' },
  { id: 'desired', title: 'Ønsket tilstand' },
  { id: 'actions', title: 'Tiltak' },
];

const tabDefs: Array<{ id: TabId; title: string; subtitle: string }> = [
  {
    id: 'now',
    title: 'Nå-tilstand',
    subtitle: 'Kartlegg dagens situasjon',
  },
  {
    id: 'desired',
    title: 'Ønsket tilstand',
    subtitle: 'Definer mål og retning',
  },
  {
    id: 'actions',
    title: 'Tiltak',
    subtitle: 'Hva skal vi gjøre?',
  },
  {
    id: 'summary',
    title: 'Oppsummering',
    subtitle: 'Forankre og evaluere',
  },
];

const topDefs: TopDef[] = [
  {
    id: 'ambisjonsprosess',
    title: 'Ambisjonsprosess',
    subs: [
      { id: 'visjon', title: 'Visjon' },
      { id: 'misjon', title: 'Misjon' },
      { id: 'delmaal', title: 'Delmål' },
    ],
  },
  {
    id: 'lagorganisering',
    title: 'Lagorganisering',
    subs: [
      { id: 'hundreAnsvar', title: '100% ansvar' },
      { id: 'samspillsoner', title: 'Samspillsoner (2-3 kritiske)' },
    ],
  },
  {
    id: 'prestasjonskultur',
    title: 'Prestasjonskultur',
    subs: [
      { id: 'verdier', title: 'Verdier' },
      { id: 'feedback', title: 'Feedback' },
    ],
  },
  {
    id: 'ledelsePersonlig',
    title: 'Ledelse/personlig utvikling',
    subs: [
      { id: 'utfordringer', title: 'Mine viktigste utfordringer' },
      { id: 'coaching', title: 'Coaching' },
    ],
  },
];

const TOP_ICONS: Record<TopId, SvgIconComponent> = {
  ambisjonsprosess: GpsFixedIcon,
  lagorganisering: GroupsOutlinedIcon,
  prestasjonskultur: BarChartOutlinedIcon,
  ledelsePersonlig: PersonOutlineIcon,
};

type FormData = Record<MidId, Record<TopId, Record<SubId, string>>>;

const STORAGE_KEY = 'devplan:v2';
const STORAGE_KEY_SUMMARY = 'devplan:v2:summary';
const STORAGE_KEY_AI_RUN = 'devplan:v2:aiRun';

const markAiRunActive = () => {
  try {
    sessionStorage.setItem(STORAGE_KEY_AI_RUN, '1');
  } catch {
    // Ignore storage failures.
  }
};

const clearAiRunActive = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY_AI_RUN);
  } catch {
    // Ignore storage failures.
  }
};

const hasAiRunActive = () => {
  try {
    return sessionStorage.getItem(STORAGE_KEY_AI_RUN) === '1';
  } catch {
    return false;
  }
};
const SUMMARY_ROWS = 3;
const SUMMARY_CELL_MIN_HEIGHT_PX = 92;
const SUMMARY_KEYWORD_MAX_LENGTH = 80;

const buildDefaultForm = (): FormData => {
  const data = {} as FormData;

  for (const mid of midDefs) {
    const topObj = {} as Record<TopId, Record<SubId, string>>;

    for (const top of topDefs) {
      const subObj = {} as Record<SubId, string>;
      for (const sub of top.subs) {
        subObj[sub.id] = '';
      }
      topObj[top.id] = subObj;
    }

    data[mid.id] = topObj;
  }

  return data;
};

const buildMockNowDesiredForm = (): FormData => {
  const data = buildDefaultForm();

  data.now.ambisjonsprosess.visjon =
    'Teamet mitt jobber mye reaktivt og venter ofte på avklaringer før de tar initiativ.';

  data.desired.ambisjonsprosess.visjon =
    'Et mer selvstendig og proaktivt team som tar initiativ og løser flere ting uten å vente på meg.';

  return data;
};

const buildMockWithActionsForm = (): FormData => {
  const data = buildMockNowDesiredForm();

  data.actions.ambisjonsprosess.visjon =
    'Gjennomfør en 45-min “visjonsworkshop” i neste teammøte. Avslutt med én konkret setning som alle kan gjenta, og henge den synlig i Teams/Slack.';

  return data;
};

const formatLastSaved = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  const hours = pad(d.getHours());
  const mins = pad(d.getMinutes());
  const day = d.getDate();
  const month = d.toLocaleString('no-NO', { month: 'long' });
  const year = d.getFullYear();
  return `${hours}:${mins}, ${day}. ${month}, ${year}`;
};

export const DevelopmentPlan: React.FC<{
  embedded?: boolean;
  circleId?: string;
}> = ({ embedded = false, circleId }) => {
  const calendarPlanUrl = developmentPlanUrl(circleId);
  const [activeMid, setActiveMid] = useState<TabId>('now');
  const [expandedTop, setExpandedTop] = useState<TopId[]>([]);

  const [form, setForm] = useState<FormData>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        return JSON.parse(raw) as FormData;
      }
    } catch {}
    return buildDefaultForm();
  });

  // Oppsummering (stikkord) er en egen visning og skal ikke automatisk fylles
  // fra Nå/Ønsket/Tiltak. Bruker/AI fyller inn stikkord her.
  const [summaryForm, setSummaryForm] = useState<FormData>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_SUMMARY);
      if (raw) {
        return JSON.parse(raw) as FormData;
      }
    } catch {}
    return buildDefaultForm();
  });

  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [backendStatus, setBackendStatus] = useState<
    'idle' | 'loading' | 'ready' | 'saving' | 'error'
  >('idle');

  const [hasClickedTiltakForslag, setHasClickedTiltakForslag] = useState(false);
  const [progressiveIsLoading, setProgressiveIsLoading] = useState(false);
  const [summaryKeywordsLoading, setSummaryKeywordsLoading] = useState(false);
  const [failedBoxes, setFailedBoxes] = useState<Record<string, string>>({});
  const [progressiveResults, setProgressiveResults] = useState<
    Record<string, HandlingsCoachV2Section>
  >({});
  const [currentProcessingBoxId, setCurrentProcessingBoxId] = useState<
    string | null
  >(null);
  const [boxJobUiStatus, setBoxJobUiStatus] = useState<
    Record<string, AnalyzeBoxJobUiStatus | 'still_running'>
  >({});
  const [mockMode, setMockMode] = useState<
    'now_desired' | 'now_desired_actions'
  >('now_desired');
  const [analysisScrollNonce, setAnalysisScrollNonce] = useState(0);
  const [aiSummaryLockedDialogOpen, setAiSummaryLockedDialogOpen] =
    useState<boolean>(false);
  const [calendarAnchorDate, setCalendarAnchorDate] = useState<Date | null>(
    null
  );
  const [placedCalendarDates, setPlacedCalendarDates] = useState<
    Record<string, Date>
  >({});
  const [addAllCalendarOpen, setAddAllCalendarOpen] = useState(false);

  const lastSavedSummaryRef = useRef<FormData | null>(null);
  const contentStartRef = useRef<HTMLDivElement | null>(null);
  const skipStepScrollRef = useRef(true);
  const formRef = useRef(form);
  const progressiveResultsRef = useRef(progressiveResults);
  const analysisSectionRef = useRef<HTMLDivElement | null>(null);
  const pendingAnalysisScrollBoxIdRef = useRef<string | null>(null);
  const unmountedRef = useRef(false);
  const analysisGenerationRef = useRef(0);
  const didResumeAiRunRef = useRef(false);
  formRef.current = form;
  progressiveResultsRef.current = progressiveResults;

  const currentUserId = useAppSelector(selectCurrentUserId);
  const skipNextUserAbortRef = useRef(true);

  const showMockButton = (() => {
    const env = String(process.env.REACT_APP_API_ENV ?? '').toLowerCase();
    return env === 'local' || env === 'staging';
  })();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const hasAtLeastOneNowDesiredPair = useMemo(() => {
    const hasText = (v: unknown) =>
      typeof v === 'string' && v.trim().length > 0;

    for (const top of topDefs) {
      for (const sub of top.subs) {
        const now = form.now[top.id][sub.id];
        const desired = form.desired[top.id][sub.id];
        if (hasText(now) && hasText(desired)) {
          return true;
        }
      }
    }
    return false;
  }, [form]);

  const hasAnyContentInTiltakBoxes = useMemo(() => {
    const hasText = (v: unknown) =>
      typeof v === 'string' && v.trim().length > 0;
    for (const top of topDefs) {
      for (const sub of top.subs) {
        if (
          hasText(form.now[top.id]?.[sub.id]) ||
          hasText(form.desired[top.id]?.[sub.id]) ||
          hasText(form.actions[top.id]?.[sub.id])
        ) {
          return true;
        }
      }
    }
    return false;
  }, [form]);

  const isSummaryFormEmpty = useMemo(() => {
    const hasText = (v: unknown) =>
      typeof v === 'string' && v.trim().length > 0;
    for (const top of topDefs) {
      for (const sub of top.subs) {
        if (
          hasText(summaryForm.now[top.id]?.[sub.id]) ||
          hasText(summaryForm.desired[top.id]?.[sub.id]) ||
          hasText(summaryForm.actions[top.id]?.[sub.id])
        ) {
          return false;
        }
      }
    }
    return true;
  }, [summaryForm]);

  const eligibleBoxIds = useMemo(() => {
    const hasText = (v: unknown) =>
      typeof v === 'string' && v.trim().length > 0;
    const ids: string[] = [];
    for (const top of topDefs) {
      for (const sub of top.subs) {
        const now = form.now[top.id][sub.id];
        const desired = form.desired[top.id][sub.id];
        if (hasText(now) && hasText(desired)) {
          ids.push(buildBoxId(top.id, sub.id));
        }
      }
    }
    return ids;
  }, [form]);

  const allBoxIds = useMemo(() => {
    const ids: string[] = [];
    for (const top of topDefs) {
      for (const sub of top.subs) {
        ids.push(buildBoxId(top.id, sub.id));
      }
    }
    return ids;
  }, []);

  const calendarSteps = useMemo(
    () =>
      collectCalendarSteps(allBoxIds, progressiveResults, (boxId) => {
        const [topId, subId] = boxId.split(':');
        const top = topDefs.find((item) => item.id === topId);
        const sub = top?.subs.find((item) => item.id === subId);
        return {
          context: `${top?.title ?? topId} – ${sub?.title ?? subId}`,
          fieldLabel: sub?.title ?? subId,
        };
      }),
    [allBoxIds, progressiveResults]
  );

  const calendarStepById = useMemo(() => {
    const map = new Map<string, CalendarStep>();
    for (const step of calendarSteps) {
      map.set(step.id, step);
    }
    return map;
  }, [calendarSteps]);

  const rememberCalendarDate = (stepId: string, date: Date) => {
    setPlacedCalendarDates((current) => ({ ...current, [stepId]: date }));
    setCalendarAnchorDate(addDays(date, 7));
  };

  const boxStatusMap = useMemo(() => {
    const map: Record<
      string,
      { eligible: boolean; missingReason: string | null }
    > = {};
    for (const top of topDefs) {
      for (const sub of top.subs) {
        const boxId = buildBoxId(top.id, sub.id);
        const now = form.now[top.id][sub.id];
        const desired = form.desired[top.id][sub.id];
        const hasNow = typeof now === 'string' && now.trim().length > 0;
        const hasDesired =
          typeof desired === 'string' && desired.trim().length > 0;
        if (hasNow && hasDesired) {
          map[boxId] = { eligible: true, missingReason: null };
        } else if (hasNow) {
          map[boxId] = {
            eligible: false,
            missingReason: 'Ikke klar for analyse, mangler Ønsket tilstand',
          };
        } else if (hasDesired) {
          map[boxId] = {
            eligible: false,
            missingReason: 'Ikke klar for analyse, mangler Nå tilstand',
          };
        } else {
          map[boxId] = {
            eligible: false,
            missingReason:
              'Ikke klar for analyse, mangler Nå og Ønsket tilstand',
          };
        }
      }
    }
    return map;
  }, [form]);

  const analyzeButtonBusy = isAnalyzeButtonBusy(
    progressiveIsLoading,
    boxJobUiStatus,
    progressiveResults
  );

  const aiSummaryLockedMessage =
    'Fyll ut minst én boks i både Nå-tilstand og Ønsket tilstand (samme felt), for eksempel Ambisjonsprosess → Visjon.';

  const [pdfViewer, setPdfViewer] = useState<{
    open: boolean;
    title: string;
    url: string | null;
    searchText?: string | null;
  }>({ open: false, title: '', url: null });

  const skipNextAutosaveRef = useRef<boolean>(true);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      analysisGenerationRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (skipNextUserAbortRef.current) {
      skipNextUserAbortRef.current = false;
      return;
    }
    analysisGenerationRef.current += 1;
    setProgressiveIsLoading(false);
    setBoxJobUiStatus({});
    clearAiRunActive();
  }, [currentUserId]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setBackendStatus('loading');
      try {
        const plan = await developmentPlanApi.getMyPlan();

        if (cancelled) {
          return;
        }

        if (plan?.data) {
          setForm(plan.data as FormData);

          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(plan.data));
          } catch {}

          if (plan.keywords) {
            const kw = plan.keywords as FormData;
            setSummaryForm(kw);
            lastSavedSummaryRef.current = JSON.parse(JSON.stringify(kw));
            try {
              localStorage.setItem(
                STORAGE_KEY_SUMMARY,
                JSON.stringify(plan.keywords)
              );
            } catch {}
          }

          if (plan.updatedAt) {
            setLastSavedAt(new Date(plan.updatedAt));
          }

          skipNextAutosaveRef.current = true;
        } else {
          skipNextAutosaveRef.current = true;
        }

        if (plan?.data && !cancelled) {
          try {
            const aiResults = await developmentPlanApi.getAiResults();
            if (cancelled) {
              return;
            }
            if (aiResults && Object.keys(aiResults).length > 0) {
              setProgressiveResults(aiResults);
              setHasClickedTiltakForslag(true);
              setActiveMid('actions');
            }
          } catch {
            if (!cancelled) {
              toast.error(
                'Kunne ikke hente lagret analyse. Klikk «Analyser tiltak» for å generere på nytt.'
              );
            }
          }
        }

        if (cancelled) {
          return;
        }

        setBackendStatus('ready');
      } catch {
        if (cancelled) {
          return;
        }
        setBackendStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
      } catch {}

      if (skipNextAutosaveRef.current) {
        skipNextAutosaveRef.current = false;
        return;
      }

      (async () => {
        try {
          setBackendStatus((s) => (s === 'loading' ? s : 'saving'));
          const saved = await developmentPlanApi.upsertPlan({
            version: 2,
            data: form,
            keywords: summaryForm,
          });

          if (saved?.updatedAt) {
            setLastSavedAt(new Date(saved.updatedAt));
          } else {
            setLastSavedAt(new Date());
          }

          setBackendStatus('ready');
          lastSavedSummaryRef.current = JSON.parse(JSON.stringify(summaryForm));
        } catch {
          setBackendStatus('error');
          setLastSavedAt(new Date());
        }
      })();
    }, 500);

    return () => clearTimeout(t);
  }, [form, summaryForm]);

  const lastSavedText = useMemo(() => {
    if (backendStatus === 'loading') {
      return 'Laster utviklingsplan...';
    }

    if (!lastSavedAt) {
      return backendStatus === 'error'
        ? 'Endringer lagres lokalt (klarte ikke å lagre til server).'
        : 'Endringer lagres automatisk.';
    }

    const prefix =
      backendStatus === 'error'
        ? 'Endringer lagres lokalt. '
        : backendStatus === 'saving'
          ? 'Lagrer... '
          : 'Endringer lagres automatisk. ';

    return `${prefix}Sist lagret ${formatLastSaved(lastSavedAt)}`;
  }, [lastSavedAt, backendStatus]);

  const setValue = (
    midId: MidId,
    topId: TopId,
    subId: SubId,
    value: string
  ) => {
    setForm((prev) => ({
      ...prev,
      [midId]: {
        ...prev[midId],
        [topId]: {
          ...prev[midId][topId],
          [subId]: value,
        },
      },
    }));
  };

  const setSummaryValue = (
    midId: MidId,
    topId: TopId,
    subId: SubId,
    value: string
  ) => {
    setSummaryForm((prev) => ({
      ...prev,
      [midId]: {
        ...prev[midId],
        [topId]: {
          ...prev[midId][topId],
          [subId]: value,
        },
      },
    }));
  };

  const onSummaryAutoComplete = async () => {
    const items: Array<{ id: string; sourceText: string }> = [];
    for (const midId of ['now', 'desired', 'actions'] as const) {
      for (const top of topDefs) {
        for (const sub of top.subs) {
          const sourceText = (form[midId][top.id][sub.id] ?? '').trim();
          const currentKeyword = (
            summaryForm[midId][top.id][sub.id] ?? ''
          ).trim();
          if (sourceText && !currentKeyword) {
            items.push({
              id: `${midId}:${top.id}:${sub.id}`,
              sourceText,
            });
          }
        }
      }
    }
    if (items.length === 0) {
      toast('Ingen tomme stikkord-felter å fylle ut.');
      return;
    }
    setSummaryKeywordsLoading(true);
    try {
      const { keyword_items } = await developmentPlanApi.generateKeywords({
        items,
      });
      setSummaryForm((prev) => {
        const next: FormData = {
          now: { ...prev.now },
          desired: { ...prev.desired },
          actions: { ...prev.actions },
        };
        for (const top of topDefs) {
          next.now[top.id] = { ...prev.now[top.id] };
          next.desired[top.id] = { ...prev.desired[top.id] };
          next.actions[top.id] = { ...prev.actions[top.id] };
        }
        for (const { id, keyword } of keyword_items) {
          const parts = id.split(':');
          if (parts.length !== 3) {
            continue;
          }
          const [midId, topId, subId] = parts as [MidId, TopId, SubId];
          const topObj = next[midId][topId as keyof typeof next.now];
          if (topObj && subId in topObj) {
            next[midId] = {
              ...next[midId],
              [topId]: { ...topObj, [subId]: keyword },
            };
          }
        }
        return next;
      });
      toast.success(`Stikkord oppdatert for ${keyword_items.length} felt.`);
    } catch {
      toast.error('Kunne ikke generere stikkord. Prøv igjen.');
    } finally {
      setSummaryKeywordsLoading(false);
    }
  };

  // Lokal persist av Oppsummering-stikkord (skilt fra plan-data i DB).
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY_SUMMARY, JSON.stringify(summaryForm));
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [summaryForm]);

  useEffect(() => {
    if (skipStepScrollRef.current) {
      return;
    }
    skipStepScrollRef.current = true;
    contentStartRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [activeMid]);

  useEffect(() => {
    if (analysisScrollNonce === 0 || !hasClickedTiltakForslag) {
      return;
    }

    const boxId = pendingAnalysisScrollBoxIdRef.current;
    const scrollToAnalysis = () => {
      const target =
        (boxId ? document.getElementById(analysisBoxDomId(boxId)) : null) ??
        analysisSectionRef.current;
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const frame = window.requestAnimationFrame(scrollToAnalysis);
    return () => window.cancelAnimationFrame(frame);
  }, [analysisScrollNonce, hasClickedTiltakForslag]);

  const isBoxEligibleForAi = (boxId: string): boolean => {
    const [topId, subId] = boxId.split(':');
    const hasText = (v: unknown) =>
      typeof v === 'string' && v.trim().length > 0;
    return (
      hasText(form.now[topId as TopId]?.[subId as SubId]) &&
      hasText(form.desired[topId as TopId]?.[subId as SubId])
    );
  };

  const boxInputMatchesCached = (
    boxId: string,
    section: HandlingsCoachV2Section | undefined
  ): boolean => {
    if (!section?.user_input) {
      return false;
    }
    const [topId, subId] = boxId.split(':');
    const trim = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
    const current = formRef.current;
    return (
      trim(current.now[topId as TopId]?.[subId as SubId]) ===
        trim(section.user_input.now) &&
      trim(current.desired[topId as TopId]?.[subId as SubId]) ===
        trim(section.user_input.desired) &&
      trim(current.actions[topId as TopId]?.[subId as SubId]) ===
        trim(section.user_input.action)
    );
  };

  const beginAnalysisRun = () => {
    analysisGenerationRef.current += 1;
    return analysisGenerationRef.current;
  };

  const clearBoxJobUi = (boxId: string) => {
    setBoxJobUiStatus((prev) => {
      if (!(boxId in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[boxId];
      return next;
    });
  };

  const analyzeBox = (boxId: string, generation: number) =>
    analyzeHandlingsCoachBox({
      boxId,
      startJob: (id) =>
        developmentPlanApi.startHandlingsCoachJob({
          scope: 'requested_only',
          boxIds: [id],
        }),
      getJob: (jobId) => developmentPlanApi.getHandlingsCoachJob(jobId),
      fetchCachedResults: () => developmentPlanApi.getAiResults(),
      matchesCurrentInput: boxInputMatchesCached,
      shouldAbort: () =>
        unmountedRef.current || analysisGenerationRef.current !== generation,
      onStatus: (status) => {
        if (
          status === 'queued' ||
          status === 'processing' ||
          status === 'retrying'
        ) {
          setBoxJobUiStatus((prev) => ({ ...prev, [boxId]: status }));
        }
      },
    });

  const applyCoachSection = (
    boxId: string,
    section: HandlingsCoachV2Section
  ) => {
    setProgressiveResults((prev) => ({
      ...prev,
      [boxId]: section,
      [section.box_id]: section,
    }));
    setFailedBoxes((prev) => {
      const next = { ...prev };
      delete next[boxId];
      delete next[section.box_id];
      return next;
    });
    clearBoxJobUi(boxId);
    clearBoxJobUi(section.box_id);
  };

  const runAnalyzeBox = async (boxId: string, generation: number) => {
    let supersededRestarts = 0;

    while (true) {
      if (
        unmountedRef.current ||
        analysisGenerationRef.current !== generation
      ) {
        return 'aborted' as const;
      }

      const outcome = await analyzeBox(boxId, generation);

      if (outcome.kind === 'aborted') {
        return 'aborted' as const;
      }

      if (outcome.kind === 'section') {
        applyCoachSection(boxId, outcome.section);
        return 'done' as const;
      }

      if (outcome.kind === 'still_running') {
        setBoxJobUiStatus((prev) => ({
          ...prev,
          [boxId]: 'still_running',
        }));
        void waitForCachedAiSection({
          boxId,
          fetchCachedResults: () => developmentPlanApi.getAiResults(),
          matchesCurrentInput: boxInputMatchesCached,
          timeoutMs: AI_COACH_BACKGROUND_WATCH_MS,
          shouldAbort: () =>
            unmountedRef.current ||
            analysisGenerationRef.current !== generation,
        }).then((recovered) => {
          if (recovered) {
            applyCoachSection(boxId, recovered);
          }
        });
        return 'still_running' as const;
      }

      if (supersededRestarts >= 2) {
        setFailedBoxes((prev) => ({
          ...prev,
          [boxId]: 'Planen ble endret. Trykk «Prøv igjen».',
        }));
        clearBoxJobUi(boxId);
        return 'done' as const;
      }

      supersededRestarts += 1;
      await developmentPlanApi.upsertPlan({
        version: 2,
        data: formRef.current,
      });
    }
  };

  const onOverallSummary = async () => {
    const generation = beginAnalysisRun();
    markAiRunActive();
    setHasClickedTiltakForslag(true);
    setFailedBoxes({});
    setProgressiveIsLoading(true);

    try {
      await developmentPlanApi.upsertPlan({
        version: 2,
        data: formRef.current,
      });

      if (
        unmountedRef.current ||
        analysisGenerationRef.current !== generation
      ) {
        return;
      }

      let cachedResults = progressiveResultsRef.current;
      try {
        cachedResults = await developmentPlanApi.getAiResults();
        if (Object.keys(cachedResults).length > 0) {
          progressiveResultsRef.current = cachedResults;
          setProgressiveResults(cachedResults);
        }
      } catch {
        cachedResults = progressiveResultsRef.current;
      }

      if (
        unmountedRef.current ||
        analysisGenerationRef.current !== generation
      ) {
        return;
      }

      const boxesToRun = eligibleBoxIds.filter(
        (boxId) =>
          isBoxEligibleForAi(boxId) &&
          !pickCachedSection(cachedResults, boxId, boxInputMatchesCached)
      );

      setBoxJobUiStatus(
        Object.fromEntries(boxesToRun.map((boxId) => [boxId, 'queued']))
      );
      setProgressiveIsLoading(false);

      if (boxesToRun.length === 0) {
        clearAiRunActive();
        return;
      }

      const outcomes = await Promise.all(
        boxesToRun.map(async (boxId) => {
          try {
            return await runAnalyzeBox(boxId, generation);
          } catch (e) {
            setFailedBoxes((prev) => ({
              ...prev,
              [boxId]: getAiCoachUserErrorMessage(e),
            }));
            clearBoxJobUi(boxId);
            return 'failed' as const;
          }
        })
      );

      if (
        analysisGenerationRef.current === generation &&
        !outcomes.includes('still_running')
      ) {
        clearAiRunActive();
      }
    } finally {
      if (analysisGenerationRef.current === generation) {
        setCurrentProcessingBoxId(null);
        setProgressiveIsLoading(false);
      }
    }
  };

  const onAnalyzeClick = () => {
    if (!hasAtLeastOneNowDesiredPair) {
      setAiSummaryLockedDialogOpen(true);
      return;
    }

    pendingAnalysisScrollBoxIdRef.current =
      eligibleBoxIds.find(
        (boxId) => !boxInputMatchesCached(boxId, progressiveResults[boxId])
      ) ??
      eligibleBoxIds[0] ??
      null;
    setAnalysisScrollNonce((nonce) => nonce + 1);
    void onOverallSummary();
  };

  const retryBox = async (boxId: string) => {
    if (!isBoxEligibleForAi(boxId)) {
      return;
    }

    markAiRunActive();
    const hasActiveAnalysisJobs = Object.values(boxJobUiStatus).some(
      (status) =>
        status === 'queued' ||
        status === 'processing' ||
        status === 'retrying' ||
        status === 'still_running'
    );
    const generation =
      analysisGenerationRef.current > 0 &&
      (analyzeButtonBusy || hasActiveAnalysisJobs)
        ? analysisGenerationRef.current
        : beginAnalysisRun();
    setFailedBoxes((prev) => {
      const next = { ...prev };
      delete next[boxId];
      return next;
    });
    setBoxJobUiStatus((prev) => ({ ...prev, [boxId]: 'queued' }));

    try {
      await developmentPlanApi.upsertPlan({
        version: 2,
        data: formRef.current,
      });
      await runAnalyzeBox(boxId, generation);
    } catch (e) {
      setFailedBoxes((prev) => ({
        ...prev,
        [boxId]: getAiCoachUserErrorMessage(e),
      }));
      clearBoxJobUi(boxId);
    } finally {
      if (analysisGenerationRef.current === generation) {
        setCurrentProcessingBoxId(null);
      }
    }
  };

  const analysisStatusLabel = (boxId: string, hasResult: boolean): string => {
    const jobUi = boxJobUiStatus[boxId];
    if (hasResult) {
      return 'Analyse ferdig, se nederst på siden';
    }
    if (jobUi === 'still_running') {
      return AI_COACH_STILL_RUNNING_USER_MESSAGE;
    }
    if (jobUi === 'retrying') {
      return AI_COACH_RETRYING_USER_MESSAGE;
    }
    if (
      currentProcessingBoxId === boxId ||
      jobUi === 'queued' ||
      jobUi === 'processing'
    ) {
      return 'Analyserer';
    }
    return 'Venter';
  };

  useEffect(() => {
    if (backendStatus !== 'ready' || didResumeAiRunRef.current) {
      return;
    }
    if (!hasAiRunActive()) {
      return;
    }
    didResumeAiRunRef.current = true;
    void onOverallSummary();
  }, [backendStatus]);

  const onMockFill = () => {
    const next =
      mockMode === 'now_desired' ? 'now_desired_actions' : 'now_desired';
    const data =
      mockMode === 'now_desired'
        ? buildMockNowDesiredForm()
        : buildMockWithActionsForm();

    // UX: Don't immediately autosave mock data to DB.
    skipNextAutosaveRef.current = true;
    setForm(data);

    toast.success(
      mockMode === 'now_desired'
        ? 'Limte inn mock i Nå-tilstand og Ønsket tilstand.'
        : 'Limte inn mock i Nå-tilstand, Ønsket tilstand og Tiltak.'
    );

    setMockMode(next);
  };

  const onDownloadAnalysis = () => {
    const hasText = (v: unknown) =>
      typeof v === 'string' && v.trim().length > 0;

    const blocks = topDefs.flatMap((top) =>
      top.subs.map((sub) => {
        const boxId = buildBoxId(top.id, sub.id);
        const section = progressiveResults[boxId];
        if (section) {
          return { kind: 'section' as const, section };
        }

        const now = form.now[top.id][sub.id];
        const desired = form.desired[top.id][sub.id];
        const action = form.actions[top.id][sub.id];
        const missingNow = !hasText(now);
        const missingDesired = !hasText(desired);

        const missing: string[] = [];
        if (missingNow) {
          missing.push('Nå-tilstand');
        }
        if (missingDesired) {
          missing.push('Ønsket tilstand');
        }

        const message =
          missing.length > 0
            ? 'Mangler "' + missing.join('" og "') + '"'
            : 'Ikke analysert ennå';

        return {
          kind: 'missing' as const,
          boxId,
          category: top.title,
          field: sub.title,
          message,
          user_input: {
            now: typeof now === 'string' ? now : '',
            desired: typeof desired === 'string' ? desired : '',
            action: typeof action === 'string' ? action : '',
          },
        };
      })
    );
    void downloadAnalysisPdf({
      intro: '',
      blocks,
      exportedAt: new Date(),
    });
  };

  const closePdfViewer = () => {
    setPdfViewer((prev) => {
      if (prev.url?.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(prev.url);
        } catch {}
      }
      return { open: false, title: '', url: null, searchText: null };
    });
  };

  const keywordsHelp = (
    <Tooltip
      arrow
      title="Kun tomme stikkordbokser fylles ut. Eksisterende tekst overskrives ikke."
    >
      <IconButton
        aria-label="Info om generering av stikkord"
        size="small"
        className={embedded ? styles.toolbarHelp : undefined}
        sx={
          embedded
            ? undefined
            : {
                ml: 0.5,
                color: '#fff',
                verticalAlign: 'middle',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.14)',
                },
              }
        }
      >
        <HelpOutlineIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const planActions = (
    <div className={styles.bannerActions}>
      {activeMid === 'summary' && (
        <>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => void downloadDevelopmentPlanPdf(summaryForm)}
            disabled={isSummaryFormEmpty}
            style={
              isSummaryFormEmpty
                ? {
                    background: '#e5e7eb',
                    color: '#374151',
                    cursor: 'not-allowed',
                  }
                : undefined
            }
          >
            Last ned oppsummering
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => void onSummaryAutoComplete()}
            disabled={summaryKeywordsLoading}
          >
            {summaryKeywordsLoading
              ? 'Genererer stikkord…'
              : 'Generer stikkord'}
          </button>
          {keywordsHelp}
        </>
      )}
      {activeMid === 'actions' && hasAnyContentInTiltakBoxes && (
        <button
          type="button"
          className={styles.primaryButton}
          onClick={onDownloadAnalysis}
        >
          Last ned analyse
        </button>
      )}
      {activeMid === 'actions' && (
        <button
          type="button"
          className={styles.primaryButton}
          onClick={onAnalyzeClick}
          disabled={analyzeButtonBusy}
          aria-disabled={!hasAtLeastOneNowDesiredPair}
          style={{
            minWidth: 140,
            background: hasAtLeastOneNowDesiredPair ? undefined : '#e5e7eb',
            color: hasAtLeastOneNowDesiredPair ? undefined : '#374151',
            cursor: hasAtLeastOneNowDesiredPair ? undefined : 'not-allowed',
            opacity: hasAtLeastOneNowDesiredPair ? undefined : 0.95,
          }}
        >
          {analyzeButtonBusy ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={14} color="inherit" />
              <span>Analyserer</span>
            </Stack>
          ) : (
            'Analyser tiltak'
          )}
        </button>
      )}
    </div>
  );

  const hasToolbarActions = activeMid === 'summary' || activeMid === 'actions';
  const activeStepIndex = tabDefs.findIndex((tab) => tab.id === activeMid);

  const goToStep = (id: TabId) => {
    if (id === activeMid) {
      return;
    }
    skipStepScrollRef.current = false;
    setActiveMid(id);
  };

  const embeddedToolbar = hasToolbarActions ? (
    <div className={styles.actionToolbar}>
      {activeMid === 'summary' && (
        <>
          <button
            type="button"
            className={`${styles.toolbarBtn} ${styles.toolbarBtnSecondary}`}
            onClick={() => void downloadDevelopmentPlanPdf(summaryForm)}
            disabled={isSummaryFormEmpty}
          >
            <FileDownloadOutlinedIcon fontSize="small" />
            Last ned oppsummering
          </button>
          <div className={styles.toolbarPrimaryGroup}>
            <button
              type="button"
              className={`${styles.toolbarBtn} ${styles.toolbarBtnPrimary}`}
              onClick={() => void onSummaryAutoComplete()}
              disabled={summaryKeywordsLoading}
            >
              {summaryKeywordsLoading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <AutoAwesomeIcon fontSize="small" />
              )}
              {summaryKeywordsLoading
                ? 'Genererer stikkord…'
                : 'Generer stikkord'}
              <Tooltip
                arrow
                title="Kun tomme stikkordbokser fylles ut. Eksisterende tekst overskrives ikke."
              >
                <span
                  className={styles.toolbarHelpInButton}
                  tabIndex={0}
                  role="note"
                  aria-label="Info om generering av stikkord"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <HelpOutlineIcon sx={{ fontSize: 16 }} />
                </span>
              </Tooltip>
            </button>
          </div>
        </>
      )}
      {activeMid === 'actions' && hasAnyContentInTiltakBoxes && (
        <button
          type="button"
          className={`${styles.toolbarBtn} ${styles.toolbarBtnSecondary}`}
          onClick={onDownloadAnalysis}
        >
          <FileDownloadOutlinedIcon fontSize="small" />
          Last ned analyse
        </button>
      )}
      {activeMid === 'actions' && (
        <button
          type="button"
          className={`${styles.toolbarBtn} ${styles.toolbarBtnPrimary} ${
            hasAtLeastOneNowDesiredPair ? '' : styles.toolbarBtnMuted
          }`}
          onClick={onAnalyzeClick}
          disabled={analyzeButtonBusy}
          aria-disabled={!hasAtLeastOneNowDesiredPair}
        >
          {analyzeButtonBusy ? (
            <CircularProgress size={14} color="inherit" />
          ) : (
            <AutoAwesomeIcon fontSize="small" />
          )}
          {analyzeButtonBusy ? 'Analyserer' : 'Analyser tiltak'}
        </button>
      )}
    </div>
  ) : null;

  return (
    <Box
      ref={contentStartRef}
      sx={{
        maxWidth: 980,
        mx: 'auto',
        pb: embedded ? 4 : 6,
      }}
    >
      {embedded ? (
        <>
          <div className={styles.embeddedHeader}>
            <h1 className={styles.embeddedTitle}>Min utviklingsplan</h1>
            <p className={styles.embeddedLead}>
              En strukturert prosess for å skape bevisst utvikling, både som
              leder og som menneske.
            </p>
            <p className={styles.embeddedLead}>
              Her definerer vi hvor du er i dag, hvor du ønsker å være, hvilke
              tiltak som skal til, og hvordan vi følger opp.
            </p>
          </div>
          <div className={styles.stepperSticky}>
            <ol className={styles.stepper} aria-label="Utviklingsplan-steg">
              {tabDefs.map((tab, index) => {
                const isActive = tab.id === activeMid;
                const isComplete = index < activeStepIndex;
                return (
                  <li
                    key={tab.id}
                    className={`${styles.stepperItem} ${
                      isComplete ? styles.stepperItemComplete : ''
                    } ${isActive ? styles.stepperItemActive : ''}`}
                  >
                    <button
                      type="button"
                      className={styles.stepperButton}
                      onClick={() => goToStep(tab.id)}
                      aria-current={isActive ? 'step' : undefined}
                      aria-label={`Steg ${index + 1} av ${tabDefs.length}: ${
                        tab.title
                      }. ${tab.subtitle}`}
                    >
                      <span
                        className={`${styles.stepperCircle} ${
                          isActive
                            ? styles.stepperCircleActive
                            : isComplete
                              ? styles.stepperCircleComplete
                              : ''
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className={styles.stepperLabel}>{tab.title}</span>
                      <span className={styles.stepperSubtitle}>
                        {tab.subtitle}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
            {embeddedToolbar}
          </div>
        </>
      ) : (
        <Oth360Banner title="Min utviklingsplan" actions={planActions} />
      )}

      <Paper
        sx={{
          borderRadius: 2,
          overflow: embedded ? 'visible' : 'hidden',
          boxShadow: embedded ? 'none' : '0 1px 2px rgba(0,0,0,0.12)',
          backgroundColor: embedded ? 'transparent' : undefined,
        }}
      >
        {!embedded && (
          <Box sx={{ px: 2, pt: 1, p: 0 }}>
            <Tabs
              value={activeMid}
              onChange={(_, v) => goToStep(v as TabId)}
              variant="scrollable"
              scrollButtons="auto"
              className={styles.tabs}
            >
              {tabDefs.map((m) => (
                <Tab
                  key={m.id}
                  value={m.id}
                  label={m.title}
                  sx={{ textTransform: 'none' }}
                  className={styles.tab}
                />
              ))}
            </Tabs>
          </Box>
        )}

        <Box sx={{ pb: 2, pt: embedded ? 0 : undefined }}>
          {activeMid === 'summary' ? (
            <Box sx={{ px: 2, pt: embedded ? 3 : 2 }}>
              {isSmallScreen ? (
                <Stack spacing={4}>
                  {topDefs.map((top) => {
                    const CategoryIcon = TOP_ICONS[top.id];
                    return (
                      <Box key={top.id}>
                        <Typography
                          className={styles.aiH2}
                          component="h2"
                          sx={{
                            mb: 1.25,
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: '#5b5b66',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <CategoryIcon sx={{ fontSize: 16 }} />
                          {top.title}
                        </Typography>

                        <Stack spacing={2.75}>
                          {top.subs.map((sub) => {
                            const nowVal =
                              summaryForm.now[top.id][sub.id]?.trim() ?? '';
                            const desiredVal =
                              summaryForm.desired[top.id][sub.id]?.trim() ?? '';
                            const actionsVal =
                              summaryForm.actions[top.id][sub.id]?.trim() ?? '';
                            const rowAllEmpty =
                              !nowVal && !desiredVal && !actionsVal;

                            const baseFieldSx = {
                              borderRadius: 2,
                              boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                              opacity: rowAllEmpty ? 0.6 : 1,
                              '& .MuiInputBase-root': { borderRadius: 2 },
                            } as const;

                            return (
                              <Box key={sub.id}>
                                <Typography
                                  className={styles.aiFørsteStegLabel}
                                  component="div"
                                  sx={{
                                    mb: 1,
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    color: '#111827',
                                    ...(rowAllEmpty ? { opacity: 0.6 } : {}),
                                  }}
                                >
                                  {sub.title}
                                </Typography>

                                <Stack spacing={2}>
                                  <TextField
                                    className={styles.cellNow}
                                    label="Nå-tilstand"
                                    InputLabelProps={{ shrink: true }}
                                    value={
                                      summaryForm.now[top.id][sub.id] ?? ''
                                    }
                                    onChange={(e) =>
                                      setSummaryValue(
                                        'now',
                                        top.id,
                                        sub.id,
                                        e.target.value
                                      )
                                    }
                                    inputProps={{
                                      maxLength: SUMMARY_KEYWORD_MAX_LENGTH,
                                    }}
                                    multiline
                                    minRows={SUMMARY_ROWS}
                                    maxRows={SUMMARY_ROWS}
                                    fullWidth
                                    size="small"
                                    placeholder=""
                                    sx={{
                                      ...baseFieldSx,
                                      '& .MuiInputBase-root': {
                                        ...baseFieldSx['& .MuiInputBase-root'],
                                        backgroundColor: '#f3f4f6',
                                        '& fieldset': { border: 'none' },
                                      },
                                    }}
                                  />
                                  <TextField
                                    className={styles.cellDesired}
                                    label="Ønsket tilstand"
                                    InputLabelProps={{ shrink: true }}
                                    value={
                                      summaryForm.desired[top.id][sub.id] ?? ''
                                    }
                                    onChange={(e) =>
                                      setSummaryValue(
                                        'desired',
                                        top.id,
                                        sub.id,
                                        e.target.value
                                      )
                                    }
                                    inputProps={{
                                      maxLength: SUMMARY_KEYWORD_MAX_LENGTH,
                                    }}
                                    multiline
                                    minRows={SUMMARY_ROWS}
                                    maxRows={SUMMARY_ROWS}
                                    fullWidth
                                    size="small"
                                    placeholder=""
                                    sx={{
                                      ...baseFieldSx,
                                      '& .MuiInputBase-root': {
                                        ...baseFieldSx['& .MuiInputBase-root'],
                                        backgroundColor: '#d1fae5',
                                        '& fieldset': { border: 'none' },
                                      },
                                    }}
                                  />
                                  <TextField
                                    className={styles.cellTiltak}
                                    label="Tiltak"
                                    InputLabelProps={{ shrink: true }}
                                    value={
                                      summaryForm.actions[top.id][sub.id] ?? ''
                                    }
                                    onChange={(e) =>
                                      setSummaryValue(
                                        'actions',
                                        top.id,
                                        sub.id,
                                        e.target.value
                                      )
                                    }
                                    inputProps={{
                                      maxLength: SUMMARY_KEYWORD_MAX_LENGTH,
                                    }}
                                    multiline
                                    minRows={SUMMARY_ROWS}
                                    maxRows={SUMMARY_ROWS}
                                    fullWidth
                                    size="small"
                                    placeholder=""
                                    sx={{
                                      ...baseFieldSx,
                                      '& .MuiInputBase-root': {
                                        ...baseFieldSx['& .MuiInputBase-root'],
                                        backgroundColor: '#fff1f2',
                                      },
                                      '& fieldset': {
                                        borderColor: '#fca5a5',
                                      },
                                    }}
                                  />
                                </Stack>
                              </Box>
                            );
                          })}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Box
                  sx={{
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    pb: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '220px 1fr 32px 1fr 32px 1fr',
                      gap: 1.25,
                      alignItems: 'stretch',
                    }}
                  >
                    <Typography component="span" sx={{ visibility: 'hidden' }}>
                      {''}
                    </Typography>
                    <Typography
                      className={styles.summaryColumnHeader}
                      component="h3"
                    >
                      Nå-tilstand
                    </Typography>
                    <Box className={styles.summaryColumnArrow}>
                      <ChevronRightIcon
                        sx={{ color: '#878793', fontSize: 18 }}
                      />
                    </Box>
                    <Typography
                      className={styles.summaryColumnHeader}
                      component="h3"
                    >
                      Ønsket tilstand
                    </Typography>
                    <Box className={styles.summaryColumnArrow}>
                      <ChevronRightIcon
                        sx={{ color: '#878793', fontSize: 18 }}
                      />
                    </Box>
                    <Typography
                      className={styles.summaryColumnHeader}
                      component="h3"
                    >
                      Tiltak
                    </Typography>

                    {topDefs.flatMap((top, topIndex) => {
                      const CategoryIcon = TOP_ICONS[top.id];
                      const header = (
                        <Box
                          key={`${top.id}-header`}
                          sx={{
                            gridColumn: '1 / -1',
                            mt: topIndex === 0 ? 2 : 4,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            className={styles.aiH2}
                            component="h2"
                            sx={{
                              mb: 0,
                              fontSize: '0.8rem',
                              fontWeight: 700,
                              letterSpacing: '0.06em',
                              textTransform: 'uppercase',
                              color: '#5b5b66',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <CategoryIcon sx={{ fontSize: 16 }} />
                            {top.title}
                          </Typography>
                        </Box>
                      );

                      const summaryBoxBase = {
                        height: '100%',
                        minHeight: SUMMARY_CELL_MIN_HEIGHT_PX,
                        display: 'flex',
                        flexDirection: 'column' as const,
                        borderRadius: 2,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                        '& .MuiInputBase-root': {
                          height: '100%',
                          minHeight: SUMMARY_CELL_MIN_HEIGHT_PX,
                          alignItems: 'flex-start',
                          flex: 1,
                          borderRadius: 2,
                          '& fieldset': { border: 'none' },
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '0.875rem',
                          color: '#374151',
                        },
                        '& textarea': {
                          height: '100% !important',
                          minHeight: '100% !important',
                          boxSizing: 'border-box',
                          fontSize: '0.875rem',
                          color: '#374151',
                        },
                      };

                      const cellSxNow = {
                        ...summaryBoxBase,
                        '& .MuiInputBase-root': {
                          ...summaryBoxBase['& .MuiInputBase-root'],
                          backgroundColor: '#e3e3e3',
                        },
                      };
                      const cellSxDesired = {
                        ...summaryBoxBase,
                        '& .MuiInputBase-root': {
                          ...summaryBoxBase['& .MuiInputBase-root'],
                          backgroundColor: '#9dcdc2',
                        },
                      };
                      const cellSxTiltak = {
                        ...summaryBoxBase,
                        '& .MuiInputBase-root': {
                          ...summaryBoxBase['& .MuiInputBase-root'],
                          backgroundColor: '#ffffff',
                          border: '1px solid #b91c1c',
                        },
                      };

                      const rows = top.subs.map((sub) => {
                        const nowVal =
                          summaryForm.now[top.id][sub.id]?.trim() ?? '';
                        const desiredVal =
                          summaryForm.desired[top.id][sub.id]?.trim() ?? '';
                        const actionsVal =
                          summaryForm.actions[top.id][sub.id]?.trim() ?? '';
                        const rowAllEmpty =
                          !nowVal && !desiredVal && !actionsVal;

                        const cellSxNowFinal = rowAllEmpty
                          ? { ...cellSxNow, opacity: 0.6 }
                          : cellSxNow;
                        const cellSxDesiredFinal = rowAllEmpty
                          ? { ...cellSxDesired, opacity: 0.6 }
                          : cellSxDesired;
                        const cellSxTiltakFinal = rowAllEmpty
                          ? { ...cellSxTiltak, opacity: 0.6 }
                          : cellSxTiltak;

                        return (
                          <React.Fragment key={`${top.id}-${sub.id}`}>
                            <Typography
                              className={styles.aiFørsteStegLabel}
                              component="div"
                              sx={{
                                pt: 0.75,
                                fontSize: '1rem',
                                fontWeight: 600,
                                color: '#111827',
                                ...(rowAllEmpty ? { opacity: 0.6 } : {}),
                              }}
                            >
                              {sub.title}
                            </Typography>

                            <TextField
                              className={styles.cellNow}
                              value={summaryForm.now[top.id][sub.id] ?? ''}
                              onChange={(e) =>
                                setSummaryValue(
                                  'now',
                                  top.id,
                                  sub.id,
                                  e.target.value
                                )
                              }
                              inputProps={{
                                maxLength: SUMMARY_KEYWORD_MAX_LENGTH,
                              }}
                              multiline
                              minRows={SUMMARY_ROWS}
                              maxRows={SUMMARY_ROWS}
                              fullWidth
                              size="small"
                              placeholder=""
                              sx={cellSxNowFinal}
                            />
                            <Box
                              className={styles.summaryArrow}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                alignSelf: 'stretch',
                              }}
                            >
                              <ChevronRightIcon
                                sx={{ color: '#878793', fontSize: 22 }}
                              />
                            </Box>

                            <TextField
                              className={styles.cellDesired}
                              value={summaryForm.desired[top.id][sub.id] ?? ''}
                              onChange={(e) =>
                                setSummaryValue(
                                  'desired',
                                  top.id,
                                  sub.id,
                                  e.target.value
                                )
                              }
                              inputProps={{
                                maxLength: SUMMARY_KEYWORD_MAX_LENGTH,
                              }}
                              multiline
                              minRows={SUMMARY_ROWS}
                              maxRows={SUMMARY_ROWS}
                              fullWidth
                              size="small"
                              placeholder=""
                              sx={cellSxDesiredFinal}
                            />
                            <Box
                              className={styles.summaryArrow}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                alignSelf: 'stretch',
                              }}
                            >
                              <ChevronRightIcon
                                sx={{ color: '#878793', fontSize: 22 }}
                              />
                            </Box>

                            <TextField
                              className={styles.cellTiltak}
                              value={summaryForm.actions[top.id][sub.id] ?? ''}
                              onChange={(e) =>
                                setSummaryValue(
                                  'actions',
                                  top.id,
                                  sub.id,
                                  e.target.value
                                )
                              }
                              inputProps={{
                                maxLength: SUMMARY_KEYWORD_MAX_LENGTH,
                              }}
                              multiline
                              minRows={SUMMARY_ROWS}
                              maxRows={SUMMARY_ROWS}
                              fullWidth
                              size="small"
                              placeholder=""
                              sx={cellSxTiltakFinal}
                            />
                          </React.Fragment>
                        );
                      });

                      return [header, ...rows];
                    })}
                  </Box>
                </Box>
              )}
              <Typography
                variant="body2"
                className={styles.summaryNote}
                sx={{
                  color: 'text.secondary',
                  mt: 2,
                  mb: 1.5,
                  maxWidth: 560,
                  // Keep as one line on desktop (old behavior),
                  // but allow wrapping on small screens to avoid overflow.
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                }}
              >
                Kun tomme stikkordbokser fylles ut når du trykker Auto-complete.
                Eksisterende tekst overskrives ikke.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.25}>
              {topDefs.map((top) => {
                const isExpanded = expandedTop.includes(top.id);
                const subs = top.subs;
                const CategoryIcon = TOP_ICONS[top.id];

                return (
                  <Accordion
                    key={top.id}
                    expanded={isExpanded}
                    onChange={(_, next) =>
                      setExpandedTop((prev) =>
                        next
                          ? [...prev, top.id]
                          : prev.filter((id) => id !== top.id)
                      )
                    }
                    disableGutters
                    className={styles.planAccordion}
                    sx={{
                      borderRadius: '8px',
                      overflow: 'hidden',
                      '& .MuiAccordionSummary-root': {
                        minHeight: 56,
                        '&:hover': { backgroundColor: '#f5f5f7' },
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                      },
                      '& .MuiAccordionSummary-expandIconWrapper': {
                        color: '#5b5b66',
                      },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <div className={styles.accordionTitleRow}>
                        <CategoryIcon className={styles.accordionIcon} />
                        <Typography
                          className={`${styles.accordion} ${styles.tittelOverordnet}`}
                        >
                          {top.title.toUpperCase()}
                        </Typography>
                      </div>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Stack spacing={1.5}>
                        {subs.map((sub) => {
                          if (activeMid === 'now' || activeMid === 'desired') {
                            const boxId = buildBoxId(top.id, sub.id);
                            const status = boxStatusMap[boxId];
                            const statusLabel =
                              status?.eligible === false
                                ? status.missingReason
                                : null;

                            return (
                              <Stack key={sub.id} spacing={0.5}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  spacing={1}
                                  sx={{ flexWrap: 'wrap' }}
                                >
                                  <Typography className={styles.tittelMellom}>
                                    {sub.title}
                                  </Typography>
                                  {statusLabel && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        opacity: 0.8,
                                        fontStyle: 'italic',
                                      }}
                                    >
                                      {statusLabel}
                                    </Typography>
                                  )}
                                </Stack>
                                <TextField
                                  aria-label={sub.title}
                                  value={form[activeMid][top.id][sub.id] ?? ''}
                                  onChange={(e) =>
                                    setValue(
                                      activeMid,
                                      top.id,
                                      sub.id,
                                      e.target.value
                                    )
                                  }
                                  multiline
                                  minRows={3}
                                  fullWidth
                                />
                              </Stack>
                            );
                          }

                          const boxId = buildBoxId(top.id, sub.id);
                          const status = boxStatusMap[boxId];
                          const isEligible = status?.eligible ?? false;
                          const hasResult = boxId in progressiveResults;
                          const statusLabel = isEligible
                            ? hasClickedTiltakForslag
                              ? analysisStatusLabel(boxId, hasResult)
                              : null
                            : (status?.missingReason ?? null);

                          return (
                            <Stack key={sub.id} spacing={0.75}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={1}
                                sx={{ flexWrap: 'wrap' }}
                              >
                                <Typography className={styles.tittelMellom}>
                                  {sub.title}
                                </Typography>
                                {statusLabel && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      opacity: 0.8,
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    {statusLabel}
                                  </Typography>
                                )}
                              </Stack>

                              {isSmallScreen ? (
                                <Stack spacing={1.25}>
                                  <TextField
                                    className={styles.cellNow}
                                    label="Nå-tilstand"
                                    InputLabelProps={{ shrink: true }}
                                    aria-label={`${sub.title} – Nå-tilstand`}
                                    value={form.now[top.id][sub.id] ?? ''}
                                    onChange={(e) =>
                                      setValue(
                                        'now',
                                        top.id,
                                        sub.id,
                                        e.target.value
                                      )
                                    }
                                    multiline
                                    minRows={3}
                                    fullWidth
                                    sx={{
                                      borderRadius: 2,
                                      boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                                      '& .MuiInputBase-root': {
                                        borderRadius: 2,
                                        backgroundColor: '#f3f4f6',
                                      },
                                      '& fieldset': { border: 'none' },
                                    }}
                                  />
                                  <TextField
                                    className={styles.cellDesired}
                                    label="Ønsket tilstand"
                                    InputLabelProps={{ shrink: true }}
                                    aria-label={`${sub.title} – Ønsket tilstand`}
                                    value={form.desired[top.id][sub.id] ?? ''}
                                    onChange={(e) =>
                                      setValue(
                                        'desired',
                                        top.id,
                                        sub.id,
                                        e.target.value
                                      )
                                    }
                                    multiline
                                    minRows={3}
                                    fullWidth
                                    sx={{
                                      borderRadius: 2,
                                      boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                                      '& .MuiInputBase-root': {
                                        borderRadius: 2,
                                        backgroundColor: '#d1fae5',
                                      },
                                      '& fieldset': { border: 'none' },
                                    }}
                                  />
                                  <TextField
                                    className={styles.cellTiltak}
                                    label="Tiltak"
                                    InputLabelProps={{ shrink: true }}
                                    aria-label={`${sub.title} – Tiltak`}
                                    value={form.actions[top.id][sub.id] ?? ''}
                                    onChange={(e) =>
                                      setValue(
                                        'actions',
                                        top.id,
                                        sub.id,
                                        e.target.value
                                      )
                                    }
                                    multiline
                                    minRows={3}
                                    fullWidth
                                    sx={{
                                      borderRadius: 2,
                                      boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                                      '& .MuiInputBase-root': {
                                        borderRadius: 2,
                                        backgroundColor: '#fff1f2',
                                      },
                                      '& fieldset': {
                                        borderColor: '#fca5a5',
                                      },
                                    }}
                                  />
                                </Stack>
                              ) : (
                                <TextField
                                  aria-label={sub.title}
                                  value={form.actions[top.id][sub.id] ?? ''}
                                  onChange={(e) =>
                                    setValue(
                                      'actions',
                                      top.id,
                                      sub.id,
                                      e.target.value
                                    )
                                  }
                                  multiline
                                  minRows={3}
                                  fullWidth
                                />
                              )}
                            </Stack>
                          );
                        })}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })}

              <Typography
                variant="caption"
                sx={{ opacity: 0.75, pt: 2 }}
                className={styles.lastSaved}
              >
                {lastSavedText}
              </Typography>
            </Stack>
          )}
        </Box>
      </Paper>

      <Box>
        {activeMid === 'actions' &&
          hasClickedTiltakForslag &&
          allBoxIds.length > 0 && (
            <Box
              ref={analysisSectionRef}
              className={styles.aiDesignContainer}
              sx={{ mt: 2 }}
            >
              {calendarSteps.length > 0 && (
                <Box className={styles.calendarAllRow}>
                  <button
                    type="button"
                    className={styles.primaryButton}
                    onClick={() => setAddAllCalendarOpen(true)}
                  >
                    Legg alle steg i kalenderen
                  </button>
                  <p className={styles.calendarNote}>
                    Ett steg per uke, fra neste arbeidsdag. Kalenderen
                    oppdateres ikke av seg selv hvis teksten endres.
                  </p>
                </Box>
              )}
              {allBoxIds.map((boxId, sectionIdx) => {
                const section = progressiveResults[boxId];
                const jobUi = boxJobUiStatus[boxId];
                const isRetrying = jobUi === 'retrying';
                const isStillRunning = jobUi === 'still_running';
                const isProcessing =
                  currentProcessingBoxId === boxId ||
                  jobUi === 'queued' ||
                  jobUi === 'processing' ||
                  isRetrying ||
                  isStillRunning;
                const processingLabel = isStillRunning
                  ? AI_COACH_STILL_RUNNING_USER_MESSAGE
                  : isRetrying
                    ? AI_COACH_RETRYING_USER_MESSAGE
                    : 'Analyserer…';
                const [topId, subId] = boxId.split(':');
                const top = topDefs.find((t) => t.id === topId);
                const sub = top?.subs.find((s) => s.id === subId);
                const categoryLabel = top?.title ?? topId;
                const fieldLabel = sub?.title ?? subId;
                const status = boxStatusMap[boxId];
                const isEligible = status?.eligible ?? false;
                const missingReason = status?.missingReason;

                const missingMessage = (() => {
                  // Prefer a short, consistent format in the UI.
                  const now = form.now[topId as TopId]?.[subId as SubId];
                  const desired =
                    form.desired[topId as TopId]?.[subId as SubId];
                  const hasText = (v: unknown) =>
                    typeof v === 'string' && v.trim().length > 0;
                  const missing: string[] = [];
                  if (!hasText(now)) {
                    missing.push('Nå-tilstand');
                  }
                  if (!hasText(desired)) {
                    missing.push('Ønsket tilstand');
                  }
                  if (missing.length === 0) {
                    return missingReason ?? 'Ikke klar for analyse';
                  }
                  if (missing.length === 1) {
                    return `Mangler "${missing[0]}"`;
                  }
                  return `Mangler "${missing[0]}" og "${missing[1]}"`;
                })();

                const badgeVariants = [
                  styles.aiBadgePurple,
                  styles.aiBadgeGreen,
                  styles.aiBadgeOrange,
                  styles.aiBadgeCyan,
                  styles.aiBadgeBlue,
                ];
                const førsteStegVariants = [
                  styles.aiFørsteStegBoksIndigo,
                  styles.aiFørsteStegBoksGreen,
                  styles.aiFørsteStegBoksOrange,
                  styles.aiFørsteStegBoksCyan,
                  styles.aiFørsteStegBoksTeal,
                ];

                if (failedBoxes[boxId]) {
                  return (
                    <Box
                      key={boxId}
                      id={analysisBoxDomId(boxId)}
                      className={`${styles.aiHovedbolk} ${styles.aiHovedbolkFailed}`}
                      sx={{
                        p: 2,
                        border: '2px solid rgba(239,68,68,0.35)',
                        borderRadius: '12px',
                        background: 'rgba(239,68,68,0.06)',
                      }}
                    >
                      <Typography className={styles.aiH2} component="h2">
                        {categoryLabel} – {fieldLabel}
                      </Typography>
                      <Typography
                        sx={{ whiteSpace: 'pre-wrap', mt: 1, mb: 1.5 }}
                      >
                        {failedBoxes[boxId]}
                      </Typography>
                      <button
                        type="button"
                        className={styles.primaryButton}
                        onClick={() => retryBox(boxId)}
                        disabled={
                          boxJobUiStatus[boxId] === 'queued' ||
                          boxJobUiStatus[boxId] === 'processing' ||
                          boxJobUiStatus[boxId] === 'retrying'
                        }
                      >
                        Prøv igjen
                      </button>
                    </Box>
                  );
                }

                if (!section && !isEligible) {
                  const nowVal =
                    form.now[topId as TopId]?.[subId as SubId]?.trim() || '';
                  const desiredVal =
                    form.desired[topId as TopId]?.[subId as SubId]?.trim() ||
                    '';
                  const actionVal =
                    form.actions[topId as TopId]?.[subId as SubId]?.trim() ||
                    '';

                  const missingForAi: Array<'now' | 'desired'> = [];
                  if (!nowVal) {
                    missingForAi.push('now');
                  }
                  if (!desiredVal) {
                    missingForAi.push('desired');
                  }

                  const aiDisabledText =
                    missingForAi.length === 2
                      ? 'Fyll ut Nå-tilstand og Ønsket tilstand (samme felt) for å få foreslåtte tiltak.'
                      : missingForAi[0] === 'desired'
                        ? 'Ønsket tilstand mangler. Fyll ut Ønsket tilstand i fanen “Ønsket tilstand” for å få foreslåtte tiltak.'
                        : 'Nå-tilstand mangler. Fyll ut Nå-tilstand i fanen “Nå-tilstand” for å få foreslåtte tiltak.';
                  return (
                    <Box
                      key={boxId}
                      id={analysisBoxDomId(boxId)}
                      className={styles.aiHovedbolk}
                      sx={{ opacity: 0.75 }}
                    >
                      <Typography className={styles.aiH2} component="h2">
                        {categoryLabel} – {fieldLabel}
                      </Typography>
                      {!(nowVal || desiredVal || actionVal) && (
                        <Typography
                          variant="body2"
                          sx={{ opacity: 0.75, fontStyle: 'italic' }}
                        >
                          {missingMessage}
                        </Typography>
                      )}
                      {(nowVal || desiredVal || actionVal) && (
                        <Box sx={{ mt: 2 }}>
                          <Typography
                            className={styles.aiH3}
                            component="h3"
                            sx={{
                              mb: 0.75,
                              fontWeight: 700,
                              opacity: 0.75,
                              color: '#111827',
                            }}
                          >
                            Du skrev
                          </Typography>
                          <Typography
                            className={styles.aiBrødtekst}
                            component="div"
                            sx={{ fontSize: '1rem', color: '#374151' }}
                          >
                            <span className={styles.aiFørsteStegLabel}>
                              Nå-tilstand:{' '}
                            </span>
                            {nowVal || 'Mangler'}
                            <br />
                            <span className={styles.aiFørsteStegLabel}>
                              Ønsket tilstand:{' '}
                            </span>
                            {desiredVal || 'Mangler'}
                            <br />
                            <span className={styles.aiFørsteStegLabel}>
                              Tiltak:{' '}
                            </span>
                            {actionVal || 'Mangler'}
                          </Typography>
                        </Box>
                      )}

                      {(nowVal || desiredVal || actionVal) && (
                        <Box className={styles.aiNesteStegBoks} sx={{ mt: 2 }}>
                          <Box className={styles.aiNesteStegInnhold}>
                            <span
                              className={styles.aiNesteStegIcon}
                              aria-hidden
                            >
                              <WarningAmberIcon
                                sx={{
                                  fontSize: 18,
                                  color: '#b45309',
                                  verticalAlign: 'middle',
                                }}
                              />
                            </span>
                            <Box>
                              <span className={styles.aiNesteStegLabel}>
                                Foreslåtte tiltak:{' '}
                              </span>
                              <span className={styles.aiNesteStegTekst}>
                                {aiDisabledText}
                              </span>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                }

                if (isProcessing && !section) {
                  return (
                    <Box
                      key={boxId}
                      id={analysisBoxDomId(boxId)}
                      className={styles.aiHovedbolk}
                      sx={{ opacity: 0.9 }}
                    >
                      <Typography className={styles.aiH2} component="h2">
                        {categoryLabel} – {fieldLabel}
                      </Typography>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mt: 0.5 }}
                      >
                        <CircularProgress size={16} />
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          {processingLabel}
                        </Typography>
                      </Stack>
                    </Box>
                  );
                }

                if (!section) {
                  if (analyzeButtonBusy) {
                    return (
                      <Box
                        key={boxId}
                        id={analysisBoxDomId(boxId)}
                        className={styles.aiHovedbolk}
                        sx={{ opacity: 0.7 }}
                      >
                        <Typography className={styles.aiH2} component="h2">
                          {categoryLabel} – {fieldLabel}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ opacity: 0.7, fontStyle: 'italic' }}
                        >
                          Venter…
                        </Typography>
                      </Box>
                    );
                  }
                  return (
                    <Box
                      key={boxId}
                      id={analysisBoxDomId(boxId)}
                      className={styles.aiHovedbolk}
                      sx={{ opacity: 0.75 }}
                    >
                      <Typography className={styles.aiH2} component="h2">
                        {categoryLabel} – {fieldLabel}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ opacity: 0.75, fontStyle: 'italic' }}
                      >
                        Ikke analysert ennå
                      </Typography>
                    </Box>
                  );
                }

                return (
                  <Box
                    key={section.box_id ?? boxId}
                    id={analysisBoxDomId(boxId)}
                    className={styles.aiHovedbolk}
                  >
                    <Typography className={styles.aiH2} component="h2">
                      {section.category} – {section.field}
                    </Typography>
                    <Box sx={{ mb: 2, mt: 3 }}>
                      <Typography
                        className={styles.aiH3}
                        component="h3"
                        sx={{
                          mb: 0.75,
                          fontWeight: 700,
                          opacity: 0.75,
                          color: '#111827',
                        }}
                      >
                        Du skrev
                      </Typography>
                      <Typography
                        className={styles.aiBrødtekst}
                        component="div"
                        sx={{ fontSize: '1rem', color: '#374151' }}
                      >
                        <span className={styles.aiFørsteStegLabel}>
                          Nå-tilstand:{' '}
                        </span>
                        {section.user_input?.now?.trim() ||
                          form.now[topId as TopId]?.[subId as SubId]?.trim() ||
                          'Mangler'}
                        <br />
                        <span className={styles.aiFørsteStegLabel}>
                          Ønsket tilstand:{' '}
                        </span>
                        {section.user_input?.desired?.trim() ||
                          form.desired[topId as TopId]?.[
                            subId as SubId
                          ]?.trim() ||
                          'Mangler'}
                        <br />
                        <span className={styles.aiFørsteStegLabel}>
                          Tiltak:{' '}
                        </span>
                        {section.user_input?.action?.trim() ||
                          form.actions[topId as TopId]?.[
                            subId as SubId
                          ]?.trim() ||
                          'Mangler'}
                      </Typography>
                    </Box>
                    {Array.isArray(section.proposed_actions) &&
                      section.proposed_actions.length > 0 && (
                        <Box sx={{ mt: 0 }}>
                          <Typography
                            className={styles.aiH3}
                            component="h3"
                            sx={{
                              mt: 3,
                              fontWeight: 700,
                              opacity: 0.75,
                              color: '#111827',
                            }}
                          >
                            Foreslåtte tiltak
                          </Typography>
                          <Box className={styles.aiTiltakListe}>
                            {section.proposed_actions.map((pa, paIdx) => {
                              const colorIdx =
                                (sectionIdx + paIdx) % badgeVariants.length;
                              return (
                                <Box
                                  key={paIdx}
                                  className={styles.aiTiltakKort}
                                >
                                  <Box className={styles.aiTiltakKortHeader}>
                                    <Typography
                                      className={styles.aiTiltakTittel}
                                      component="h3"
                                    >
                                      {pa.title}
                                    </Typography>
                                  </Box>
                                  <Typography className={styles.aiBrødtekst}>
                                    {pa.description}
                                  </Typography>
                                  {pa.first_step && (
                                    <Box
                                      className={`${styles.aiFørsteStegBoks} ${førsteStegVariants[colorIdx]}`}
                                    >
                                      <Box
                                        className={styles.aiFørsteStegInnhold}
                                      >
                                        <span
                                          className={styles.aiFørsteStegLabel}
                                        >
                                          Første steg:
                                        </span>
                                        <span
                                          className={styles.aiArrow}
                                          aria-hidden
                                        >
                                          →
                                        </span>
                                        <span
                                          className={styles.aiFørsteStegTekst}
                                        >
                                          {pa.first_step}
                                        </span>
                                        {calendarStepById.get(
                                          `${boxId}:first:${paIdx}`
                                        ) && (
                                          <AddStepToCalendar
                                            step={
                                              calendarStepById.get(
                                                `${boxId}:first:${paIdx}`
                                              ) as CalendarStep
                                            }
                                            planUrl={calendarPlanUrl}
                                            defaultDate={
                                              placedCalendarDates[
                                                `${boxId}:first:${paIdx}`
                                              ] ?? calendarAnchorDate
                                            }
                                            onPlaced={(date) =>
                                              rememberCalendarDate(
                                                `${boxId}:first:${paIdx}`,
                                                date
                                              )
                                            }
                                          />
                                        )}
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                    {section.user_input?.action && section.review && (
                      <Box sx={{ mt: 3 }}>
                        <Typography
                          className={styles.aiVurderingTittel}
                          component="h3"
                          sx={{
                            mt: 3,
                            fontWeight: 700,
                            opacity: 0.75,
                            color: '#111827',
                          }}
                        >
                          Vurdering av ditt tiltak
                        </Typography>
                        <Box className={styles.aiVurderingBoks}>
                          {Array.isArray(section.review.likes) &&
                            section.review.likes.length > 0 && (
                              <Box className={styles.aiVurderingSeksjon}>
                                <Typography
                                  className={styles.aiH4}
                                  component="h4"
                                >
                                  Styrker:
                                </Typography>
                                <Box component="ul" className={styles.aiListe}>
                                  {section.review.likes.map((l, i) => (
                                    <li key={i} className={styles.aiListeItem}>
                                      {l}
                                    </li>
                                  ))}
                                </Box>
                              </Box>
                            )}
                          {Array.isArray(section.review.concerns) &&
                            section.review.concerns.length > 0 && (
                              <Box className={styles.aiVurderingSeksjon}>
                                <Typography
                                  className={styles.aiH4}
                                  component="h4"
                                >
                                  Bekymringer:
                                </Typography>
                                <Box component="ul" className={styles.aiListe}>
                                  {section.review.concerns.map((c, i) => (
                                    <li key={i} className={styles.aiListeItem}>
                                      {c}
                                    </li>
                                  ))}
                                </Box>
                              </Box>
                            )}
                          {Array.isArray(section.review.questions) &&
                            section.review.questions.length > 0 && (
                              <Box className={styles.aiVurderingSeksjon}>
                                <Typography
                                  className={styles.aiH4}
                                  component="h4"
                                >
                                  Spørsmål:
                                </Typography>
                                <Box component="ul" className={styles.aiListe}>
                                  {section.review.questions.map((q, i) => (
                                    <li key={i} className={styles.aiListeItem}>
                                      {q}
                                    </li>
                                  ))}
                                </Box>
                              </Box>
                            )}
                          {section.review.suggested_rewrite && (
                            <Box className={styles.aiForslagContainer}>
                              <Typography
                                className={styles.aiH4}
                                component="h4"
                              >
                                Forslag til forbedring:
                              </Typography>
                              <Box
                                className={styles.aiForslagBoks}
                                sx={{ whiteSpace: 'pre-wrap' }}
                              >
                                {section.review.suggested_rewrite}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    {section.next_step && (
                      <Box className={styles.aiNesteStegBoks} sx={{ mt: 2 }}>
                        <Box className={styles.aiNesteStegInnhold}>
                          <span className={styles.aiNesteStegIcon} aria-hidden>
                            💡
                          </span>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <span className={styles.aiNesteStegLabel}>
                              Neste steg:{' '}
                            </span>
                            <span className={styles.aiNesteStegTekst}>
                              {section.next_step}
                            </span>
                          </Box>
                          {calendarStepById.get(`${boxId}:next`) && (
                            <AddStepToCalendar
                              step={
                                calendarStepById.get(
                                  `${boxId}:next`
                                ) as CalendarStep
                              }
                              planUrl={calendarPlanUrl}
                              defaultDate={
                                placedCalendarDates[`${boxId}:next`] ??
                                calendarAnchorDate
                              }
                              onPlaced={(date) =>
                                rememberCalendarDate(`${boxId}:next`, date)
                              }
                            />
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

        <StepNavigation
          steps={tabDefs}
          activeId={activeMid}
          onNavigate={goToStep}
        />

        {showMockButton && (
          <Paper
            className={styles.mockPanel}
            sx={{
              p: 2,
              borderRadius: 2,
              boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
              mt: 2,
              border: '1px solid rgba(2,6,23,0.12)',
              background: 'rgba(2,6,23,0.03)',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              sx={{ flexWrap: 'wrap' }}
            >
              <Box>
                <Typography sx={{ fontWeight: 900 }}>Mock</Typography>
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.75, display: 'block' }}
                >
                  Kun tilgjengelig i local/staging.
                </Typography>
              </Box>

              <button
                type="button"
                className={styles.primaryButton}
                onClick={onMockFill}
                title={
                  mockMode === 'now_desired'
                    ? 'Limer inn mock i Nå-tilstand og Ønsket tilstand'
                    : 'Limer inn mock i Nå-tilstand, Ønsket tilstand og Tiltak'
                }
              >
                Mock
              </button>
            </Stack>
          </Paper>
        )}
      </Box>

      <AddAllStepsDialog
        open={addAllCalendarOpen}
        steps={calendarSteps}
        planUrl={calendarPlanUrl}
        initialStart={calendarAnchorDate}
        onClose={() => setAddAllCalendarOpen(false)}
        onPlaced={(entries) => {
          setPlacedCalendarDates((current) => {
            const next = { ...current };
            for (const entry of entries) {
              next[entry.id] = entry.date;
            }
            return next;
          });
          const last = entries[entries.length - 1];
          if (last) {
            setCalendarAnchorDate(addDays(last.date, 7));
          }
        }}
      />

      <Dialog
        open={aiSummaryLockedDialogOpen}
        onClose={() => setAiSummaryLockedDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Tiltak er låst</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ whiteSpace: 'pre-wrap' }}>
            {aiSummaryLockedMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={() => setAiSummaryLockedDialogOpen(false)}
          >
            OK
          </button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={pdfViewer.open}
        onClose={closePdfViewer}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          {pdfViewer.title}
          <IconButton
            onClick={closePdfViewer}
            aria-label="Lukk"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: '75vh' }}>
          {pdfViewer.searchText && (
            <Box sx={{ p: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Søk i PDF: <strong>{pdfViewer.searchText}</strong>
              </Typography>
            </Box>
          )}
          {pdfViewer.url ? (
            <iframe
              title={pdfViewer.title}
              src={pdfViewer.url}
              style={{ width: '100%', height: '75vh', border: 0 }}
            />
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography>Ingen PDF å vise.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
